import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../../chart/bodygraph/hd-content-language.service';
import {
  HD_PLANET_ORDER_STANDARD,
  HdPlanetKey,
  getHdPlanetDisplayConfig,
} from '../../chart/bodygraph/planet-grid/hd-planet-display-config';
import {
  HD_TRANSIT_LEGEND_CATEGORY_ORDER,
  HD_TRANSIT_LEGEND_LABEL_KEYS,
  HdTransitLegendCategory,
  legendCategoryOfKey,
} from '../transit-legend.utils';
import { HdTransitLegendScope, HdTransitsViewStateService } from '../transits-view-state.service';

const I18N_PREFIX = 'humanDesign.chart.transits.panel';

/** Weergave-model van één legenda-/filter-item (vinkje + kleurbolletje + label). */
interface HdTransitLegendFilterItemVm {
  readonly category: HdTransitLegendCategory;
  readonly color: string;
  readonly label: string;
  readonly enabled: boolean;
}

/** Alle categorieën van de standaard planeetset (+ Chiron) op volgorde. */
function categoriesForPlanets(showChiron: boolean): readonly HdTransitLegendCategory[] {
  const keys: readonly HdPlanetKey[] = showChiron ? [...HD_PLANET_ORDER_STANDARD, 'chiron'] : HD_PLANET_ORDER_STANDARD;
  return HD_TRANSIT_LEGEND_CATEGORY_ORDER.filter(category =>
    keys.some(key => legendCategoryOfKey(key) === category),
  );
}

/**
 * Gedeelde filter-legende van de transits-feature: één aanvinkbaar item per
 * planeet-categorie (kleurbolletje + label) plus een "Selecteer/Deselecteer
 * alle"-knop (eBtn xs, zoals de shortcut-bar-knoppen). De staat leeft in
 * {@link HdTransitsViewStateService}: met `scope="shared"` (default) stuurt hij
 * zowel het Nu & Straks-panel (rijen/kaarten) als de ephemeris-lanes — beide
 * secties tonen daarom dezelfde legende; met `scope="vertical"` de eigen
 * filter van de verticale dag-flow (standaard enkel de zon).
 */
@Component({
  selector: 'hd-transit-legend-filter',
  imports: [SharedModule],
  templateUrl: './hd-transit-legend-filter.component.html',
  styleUrl: './hd-transit-legend-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdTransitLegendFilterComponent {
  readonly showChiron = input.required<boolean>();
  readonly scope = input<HdTransitLegendScope>('shared');

  private readonly viewState = inject(HdTransitsViewStateService);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  private readonly lang = computed(() => {
    // Meelezen zodat de eerste translate ná een async translation-load opnieuw
    // evalueert (zie HdContentLanguageService.translationsLoadedTick).
    this.contentLang.translationsLoadedTick();
    return this.contentLang.effectiveLang();
  }, HD_LANG_RECOMPUTE_ON_LOAD);

  /** De filter van de eigen weergave (horizontaal gedeeld, of die van de verticale dag-flow). */
  private readonly enabledCategories = computed(() => this.viewState.enabledLegendCategoriesFor(this.scope())());

  protected readonly legendItems = computed<readonly HdTransitLegendFilterItemVm[]>(() => {
    const enabledCategories = this.enabledCategories();
    return categoriesForPlanets(this.showChiron()).map(category => ({
      category,
      // De kleur komt van de eerste planeet van de categorie — identiek aan
      // de `stripeColor` uit de display-config.
      color: this.categoryColor(category),
      label: this.t(HD_TRANSIT_LEGEND_LABEL_KEYS[category]),
      enabled: enabledCategories.has(category),
    }));
  });

  /** true als álle categorieën uitgevinkt zijn — dan wordt de knop "Selecteer alle". */
  protected readonly allCategoriesDeselected = computed(() => {
    const enabled = this.enabledCategories();
    return categoriesForPlanets(this.showChiron()).every(category => !enabled.has(category));
  });

  protected readonly toggleAllLabelKey = computed(() =>
    this.allCategoriesDeselected() ? 'selectAll' : 'deselectAll',
  );

  /** Font Awesome-naam (zonder fa-prefix, zonder kader) voor de eBtn van de toggle-all. */
  protected readonly toggleAllIcon = computed(() =>
    this.allCategoriesDeselected() ? 'check' : 'xmark',
  );

  protected toggleLegendCategory(category: HdTransitLegendCategory): void {
    this.viewState.toggleLegendCategoryFor(this.scope(), category);
  }

  protected toggleAllCategories(): void {
    this.viewState.setAllLegendCategoriesFor(this.scope(), this.allCategoriesDeselected());
  }

  protected t(key: string, params?: Record<string, unknown>): string {
    return this.transloco.translate(`${I18N_PREFIX}.${key}`, params, this.lang());
  }

  private categoryColor(category: HdTransitLegendCategory): string {
    const key = [...HD_PLANET_ORDER_STANDARD, 'chiron' as const].find(
      planetKey => legendCategoryOfKey(planetKey) === category,
    );
    return key != null ? getHdPlanetDisplayConfig(key).stripeColor : '';
  }
}
